import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';
import { logEvent, Events, setUserProperties } from '../services/analytics';

const ADMIN_EMAILS = process.env.REACT_APP_ADMIN_EMAILS.split(',');
const AccountContext = createContext(null);
 
export function AccountProvider({ children }) {  
    const [currentAccountId, setCurrentAccountId] = useState(
        localStorage.getItem('currentAccountId')
    );
    const [userData, setUserData] = useState(() => {
        // Initialize from localStorage
        const storedAccountId = localStorage.getItem('currentAccountId');
        const storedEmail = localStorage.getItem('userEmail');
        const storedName = localStorage.getItem('userName');
        const storedMsgSID = localStorage.getItem('messagingServiceSID');

        if (storedAccountId && storedEmail && storedName) {
            return {
                accountId: storedAccountId,
                email: storedEmail,
                name: storedName,
                messagingServiceSID: storedMsgSID,
                isAdmin: ADMIN_EMAILS.includes(storedEmail)
            };
        }
        return null;
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setIsLoading(false);
                clearUserData(); // Clear data if no token exists
                return;
            }

            try {
                const response = await api.get('/home');
                const { user } = response;
                if (user) {
                    updateUserData(user);
                } else {
                    clearUserData();
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                clearUserData();
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();

        // Add event listener for storage changes
        const handleStorageChange = (e) => {
            if (e.key === 'authToken') {
                if (e.newValue) {
                    initializeAuth();
                } else {
                    clearUserData();
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const updateUserData = (data) => {
        if (data) {
            const newUserData = {
                ...data,
                isAdmin: ADMIN_EMAILS.includes(data.email)
            };
            
            // Track user login and set user properties
            logEvent('User', Events.USER_LOGGED_IN, data.email);
            setUserProperties({
                userType: newUserData.isAdmin ? 'admin' : 'user',
                accountId: data.accountId
            });
            
            localStorage.setItem('currentAccountId', data.accountId);
            localStorage.setItem('userEmail', data.email);
            localStorage.setItem('userName', data.name);
            localStorage.setItem('messagingServiceSID', data.messagingServiceSID);
            setCurrentAccountId(data.accountId);
            setUserData(newUserData);
            setIsLoading(false);
        } else {
            // Track logout
            logEvent('User', Events.USER_LOGGED_OUT);
            clearUserData();
        }
    };

    const clearUserData = () => {
        localStorage.removeItem('currentAccountId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('messagingServiceSID');
        localStorage.removeItem('authToken');
        setCurrentAccountId(null);
        setUserData(null);
    };

    if (isLoading) {
        return <div>Loading...</div>; // Or your loading component
    }

    return (
        <AccountContext.Provider value={{ 
            currentAccountId, 
            setCurrentAccountId,
            userData, 
            setUserData: updateUserData,
            clearUserData
        }}>
            {children}
        </AccountContext.Provider>
    );
}

export function useAccount() {
    const context = useContext(AccountContext);
    if (!context) {
        throw new Error('useAccount must be used within an AccountProvider');
    }
    return context;
} 